import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Box,
  Card,
  CardContent,
  Avatar,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Button,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { getTenantsLoginHistory } from "../../../Services/TechnicalSupportApi";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import DateRangeIcon from "@mui/icons-material/DateRange";
import FilterListIcon from "@mui/icons-material/FilterList";
import PersonIcon from "@mui/icons-material/Person";
import LoginIcon from "@mui/icons-material/Login";
import ComputerIcon from "@mui/icons-material/Computer";
import PublicIcon from "@mui/icons-material/Public";

const ClintsLoginHistory = () => {
  const [loginHistory, setLoginHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [dateFilter, setDateFilter] = useState({
    startDate: "",
    endDate: ""
  });
  const [tenantFilter, setTenantFilter] = useState("");

  useEffect(() => {
    const fetchLoginHistory = async () => {
      try {
        const response = await getTenantsLoginHistory();

        const tenantsData = response?.data?.tenants;
        const status = response?.data?.status;

        if (status === "success" && tenantsData && typeof tenantsData === "object") {
          const history = Object.entries(tenantsData).flatMap(([tenant, data]) =>
            Array.isArray(data.logs)
              ? data.logs.map((log) => ({
                  tenant,
                  loginAt: log.login_at,
                  ipAddress: log.ip_address,
                  userAgent: log.user_agent,
                }))
              : []
          );

          setLoginHistory(history);
        } else {
          console.warn("Invalid login history data format:", response);
          setLoginHistory([]);
        }
      } catch (error) {
        console.error("Error fetching login history:", error);
        setError("فشل في جلب بيانات تسجيل الدخول");
      } finally {
        setLoading(false);
      }
    };

    fetchLoginHistory();
  }, []);

  // دالة البحث والفلترة
  const filteredHistory = loginHistory.filter((log) => {
    const query = searchQuery.toLowerCase();
    const matchesSearch = 
      log.tenant?.toLowerCase().includes(query) ||
      log.ipAddress?.toLowerCase().includes(query) ||
      log.userAgent?.toLowerCase().includes(query);

    // فلترة التاريخ
    let matchesDate = true;
    if (dateFilter.startDate || dateFilter.endDate) {
      const logDate = new Date(log.loginAt);
      logDate.setHours(0, 0, 0, 0);

      if (dateFilter.startDate) {
        const startDate = new Date(dateFilter.startDate);
        startDate.setHours(0, 0, 0, 0);
        matchesDate = matchesDate && logDate >= startDate;
      }

      if (dateFilter.endDate) {
        const endDate = new Date(dateFilter.endDate);
        endDate.setHours(23, 59, 59, 999);
        matchesDate = matchesDate && logDate <= endDate;
      }
    }

    // فلترة التينانت
    const matchesTenant = tenantFilter === "" || log.tenant === tenantFilter;

    return matchesSearch && matchesDate && matchesTenant;
  });

  const handleClearSearch = () => {
    setSearchQuery("");
  };

  const handleClearDateFilter = () => {
    setDateFilter({
      startDate: "",
      endDate: ""
    });
  };

  const handleClearAllFilters = () => {
    setSearchQuery("");
    setDateFilter({
      startDate: "",
      endDate: ""
    });
    setTenantFilter("");
  };

  const isFiltered = searchQuery || dateFilter.startDate || dateFilter.endDate || tenantFilter;

  // استخراج جميع أسماء التينانت الفريدة
  const uniqueTenants = [...new Set(loginHistory.map(log => log.tenant))].filter(Boolean).sort();

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <Avatar sx={{ bgcolor: '#2196f3' }}>
          <LoginIcon />
        </Avatar>
        <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#333' }}>
          سجل تسجيل دخول المستخدمين
        </Typography>
        {!loading && !error && (
          <Chip 
            label={`${loginHistory.length} سجل`}
            color="primary"
            sx={{ fontWeight: 'bold', ml: 1 }}
          />
        )}
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ ml: 2 }}>جاري تحميل البيانات...</Typography>
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ fontSize: '1.1rem', py: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      ) : loginHistory.length > 0 ? (
        <>
          {/* شريط البحث */}
          <Card 
            variant="outlined" 
            sx={{ 
              mb: 2, 
              bgcolor: '#f8f9fa',
              border: '2px solid #e3f2fd'
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: '#1976d2' }}>
                  <SearchIcon />
                </Avatar>
                <TextField
                  fullWidth
                  placeholder="ابحث في السجلات (اسم التينانت، IP، وكيل المستخدم...)"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  variant="outlined"
                  sx={{ 
                    bgcolor: 'white',
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: '#1976d2',
                      },
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="primary" />
                      </InputAdornment>
                    ),
                    endAdornment: searchQuery && (
                      <InputAdornment position="end">
                        <Tooltip title="مسح البحث">
                          <IconButton
                            onClick={handleClearSearch}
                            edge="end"
                            size="small"
                            sx={{ 
                              bgcolor: '#ffebee',
                              '&:hover': {
                                bgcolor: '#ffcdd2'
                              }
                            }}
                          >
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            </CardContent>
          </Card>

          {/* فلتر التاريخ */}
          <Card 
            variant="outlined" 
            sx={{ 
              mb: 2, 
              bgcolor: '#fff3e0',
              border: '2px solid #ffe0b2'
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Avatar sx={{ bgcolor: '#ff9800' }}>
                  <FilterListIcon />
                </Avatar>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, flexWrap: 'wrap' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 200 }}>
                    <DateRangeIcon sx={{ color: '#ff9800' }} />
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#ff9800' }}>
                      من:
                    </Typography>
                    <TextField
                      type="date"
                      size="small"
                      value={dateFilter.startDate}
                      onChange={(e) => setDateFilter(prev => ({ ...prev, startDate: e.target.value }))}
                      sx={{ 
                        bgcolor: 'white',
                        minWidth: 150
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 200 }}>
                    <DateRangeIcon sx={{ color: '#ff9800' }} />
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#ff9800' }}>
                      إلى:
                    </Typography>
                    <TextField
                      type="date"
                      size="small"
                      value={dateFilter.endDate}
                      onChange={(e) => setDateFilter(prev => ({ ...prev, endDate: e.target.value }))}
                      sx={{ 
                        bgcolor: 'white',
                        minWidth: 150
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Box>
                  {(dateFilter.startDate || dateFilter.endDate) && (
                    <Tooltip title="مسح فلتر التاريخ">
                      <IconButton
                        onClick={handleClearDateFilter}
                        size="small"
                        sx={{ 
                          bgcolor: '#ffebee',
                          '&:hover': {
                            bgcolor: '#ffcdd2'
                          }
                        }}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>
              </CardContent>
            </Card>

            {/* فلتر التينانت */}
            <Card 
            variant="outlined" 
            sx={{ 
              mb: 3, 
              bgcolor: '#e8f5e9',
              border: '2px solid #c8e6c9'
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Avatar sx={{ bgcolor: '#4caf50' }}>
                  <PersonIcon />
                </Avatar>
                
                {/* فلتر التينانت */}
                <Box sx={{ minWidth: 300, flex: 1 }}>
                  <FormControl fullWidth size="small" sx={{ bgcolor: 'white' }}>
                    <InputLabel>فلترة حسب التينانت</InputLabel>
                    <Select
                      value={tenantFilter}
                      label="فلترة حسب التينانت"
                      onChange={(e) => setTenantFilter(e.target.value)}
                    >
                      <MenuItem value="">
                        <em>جميع التينانتات</em>
                      </MenuItem>
                      {uniqueTenants.map((tenant) => (
                        <MenuItem key={tenant} value={tenant}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PersonIcon fontSize="small" color="success" />
                            {tenant}
                            <Chip 
                              label={loginHistory.filter(log => log.tenant === tenant).length}
                              size="small"
                              color="success"
                            />
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                {tenantFilter && (
                  <Tooltip title="مسح فلتر التينانت">
                    <IconButton
                      onClick={() => setTenantFilter("")}
                      size="small"
                      sx={{ 
                        bgcolor: '#ffebee',
                        '&:hover': {
                          bgcolor: '#ffcdd2'
                        }
                      }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}

                {/* عرض النتائج وزر مسح الكل */}
                {isFiltered && (
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Chip 
                      label={`${filteredHistory.length} نتيجة`}
                      color="success"
                      sx={{ fontWeight: 'bold' }}
                    />
                    <Tooltip title="مسح جميع الفلاتر">
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        startIcon={<ClearIcon />}
                        onClick={handleClearAllFilters}
                        sx={{ fontWeight: 'bold' }}
                      >
                        مسح الكل
                      </Button>
                    </Tooltip>
                  </Box>
                )}
              </Box>

              {/* عرض الفلاتر النشطة */}
              {(tenantFilter) && (
                <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                  {tenantFilter && (
                    <Chip 
                      label={`التينانت: ${tenantFilter}`}
                      color="success"
                      onDelete={() => setTenantFilter("")}
                      sx={{ fontWeight: 'bold' }}
                    />
                  )}
                </Box>
              )}
            </CardContent>
          </Card>

          {filteredHistory.length > 0 ? (
            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, border: '1px solid #e0e0e0' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem' }}>اسم التينانت</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem' }}>تاريخ تسجيل الدخول</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem' }}>عنوان IP</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem' }}>وكيل المستخدم</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredHistory.map((log, idx) => (
                    <TableRow 
                      key={idx}
                      sx={{ 
                        '&:hover': { 
                          bgcolor: '#f9f9f9',
                          transition: 'all 0.2s'
                        }
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ bgcolor: '#4caf50', width: 32, height: 32 }}>
                            <PersonIcon fontSize="small" />
                          </Avatar>
                          <Typography sx={{ fontWeight: 'bold' }}>
                            {log.tenant}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <DateRangeIcon sx={{ color: '#666', fontSize: 18 }} />
                          <Typography variant="body2">
                            {new Date(log.loginAt).toLocaleString("ar")}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <PublicIcon sx={{ color: '#666', fontSize: 18 }} />
                          <Chip 
                            label={log.ipAddress}
                            size="small"
                            variant="outlined"
                            sx={{ fontFamily: 'monospace' }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 400 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <ComputerIcon sx={{ color: '#666', fontSize: 18 }} />
                          <Typography variant="body2" color="text.secondary">
                            {log.userAgent}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ textAlign: 'center', py: 8, bgcolor: '#fff3e0', borderRadius: 2, border: '2px dashed #ff9800' }}>
              <SearchIcon sx={{ fontSize: 80, color: '#ff9800', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                لا توجد نتائج للبحث
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                جرب تغيير معايير البحث أو الفلترة
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                {searchQuery && (
                  <Chip 
                    label={`البحث: "${searchQuery}"`}
                    color="warning"
                    onDelete={handleClearSearch}
                    sx={{ fontWeight: 'bold' }}
                  />
                )}
                {dateFilter.startDate && (
                  <Chip 
                    label={`من: ${new Date(dateFilter.startDate).toLocaleDateString('ar')}`}
                    color="warning"
                    sx={{ fontWeight: 'bold' }}
                  />
                )}
                {dateFilter.endDate && (
                  <Chip 
                    label={`إلى: ${new Date(dateFilter.endDate).toLocaleDateString('ar')}`}
                    color="warning"
                    sx={{ fontWeight: 'bold' }}
                  />
                )}
                {tenantFilter && (
                  <Chip 
                    label={`التينانت: ${tenantFilter}`}
                    color="warning"
                    onDelete={() => setTenantFilter("")}
                    sx={{ fontWeight: 'bold' }}
                  />
                )}
              </Box>
              <Button
                variant="contained"
                color="warning"
                startIcon={<ClearIcon />}
                onClick={handleClearAllFilters}
                sx={{ mt: 2, fontWeight: 'bold' }}
              >
                مسح جميع الفلاتر
              </Button>
            </Box>
          )}
        </>
      ) : (
        <Box sx={{ textAlign: 'center', py: 8, bgcolor: '#f5f5f5', borderRadius: 2 }}>
          <LoginIcon sx={{ fontSize: 80, color: '#bdbdbd', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            لا توجد بيانات تسجيل دخول
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ClintsLoginHistory;
